import React from "react";
import {Alert, Card, Col, Row} from "react-bootstrap";
import useStatisticsData from "../Statistics/useStatisticsData.js";
import {formatNumber, formatPercent} from "../Statistics/format.js";
import {bereichLabel, formatDuration} from "./format.js";
import HelpTooltip from "../HelpTooltip.jsx";

/** Pflicht-Hinweis an der Kennzahl "aktive Zeit" (Spezifikation Abschnitt
 * 5): die Zahl misst NUR die Dichte der Klicks im Ordermanagement selbst -
 * Telefonate, Arbeit in Weclapp oder im Lager fließen nicht ein und dürfen
 * nicht als Maß für Arbeitsleistung gelesen werden. Thorsten ist das
 * ausdrücklich wichtig, deshalb steht der Hinweis direkt an der Kachel,
 * nicht nur in der Dokumentation. */
const AKTIVE_ZEIT_HINWEIS =
    "Misst, wie dicht im Ordermanagement gearbeitet wird. Telefonate, Arbeit in Weclapp " +
    "oder im Lager sind darin nicht enthalten - die Zahl ist kein Maß für Arbeitsleistung.";

/**
 * Kachelreihe mit den zentralen Kennzahlen des gewählten Zeitraums. Anders
 * als bei einem Alarm-Widget ist "0" hier ein echtes, aussagekräftiges
 * Ergebnis (z. B. "0 aktive Mitarbeiter" an einem Tag ohne Zugriffe) und
 * wird gezeigt statt versteckt - versteckt wird die Zeile nur, solange der
 * Ladezustand unbekannt ist oder der Endpunkt gar nichts liefert (siehe
 * globale Vorgabe "leere Bausteine").
 *
 * Die "vergleich"-Angabe aus der Spezifikation wird hier bewusst noch
 * nicht ausgewertet - ihre Feldform ist dort nur als Platzhalter "{...}"
 * benannt; sobald sie feststeht, kann sie wie in KpiCards.jsx
 * (Verkaufsstatistik) als Veränderungspfeil ergänzt werden.
 */
export default function NutzungKennzahlen({filters}) {
    const {data, loading, error} = useStatisticsData("/nutzung/daten/kennzahlen", filters);

    if (loading) {
        return null;
    }
    if (error) {
        return <Alert variant="danger" className="mb-0">{error}</Alert>;
    }
    if (!data) {
        return null;
    }

    const kacheln = [
        {label: "Aktive Mitarbeiter", value: formatNumber(data.aktive_nutzer)},
        {label: "Ø aktive Zeit / Tag", value: formatDuration(data.aktive_zeit_schnitt_sekunden), hinweis: AKTIVE_ZEIT_HINWEIS},
        {label: "Aktionen", value: formatNumber(data.aktionen)},
        {label: "Datenänderungen", value: formatNumber(data.aenderungen)},
        {
            label: "Meistgenutzter Bereich",
            value: data.top_bereich ? bereichLabel(data.top_bereich.bereich) : "–",
            sub: data.top_bereich?.anteil != null ? `${formatPercent(data.top_bereich.anteil * 100)} aller Ereignisse` : null,
        },
    ];

    return (
        <Row xs={1} sm={2} lg={5} className="g-3">
            {kacheln.map((kachel) => (
                <Col key={kachel.label}>
                    <Card className="h-100 shadow-sm">
                        <Card.Body>
                            <div className="text-muted small text-uppercase d-inline-flex align-items-center gap-1">
                                {kachel.label}
                                {kachel.hinweis && (
                                    <HelpTooltip placement="top" size={0.75} id={`hinweis-${kachel.label}`} ariaLabel={`Hinweis zu ${kachel.label}`}>
                                        {kachel.hinweis}
                                    </HelpTooltip>
                                )}
                            </div>
                            <div className="fs-4 fw-semibold lh-sm mt-1">{kachel.value}</div>
                            {kachel.sub && <div className="small text-muted mt-1">{kachel.sub}</div>}
                        </Card.Body>
                    </Card>
                </Col>
            ))}
        </Row>
    );
}
